import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { AuthErrorMessage } from '../components/auth-mfa/AuthErrorMessage';
import { MfaSetupCompleteNotice } from '../components/auth-mfa/MfaSetupCompleteNotice';
import { SignInCompletionFailure } from '../components/auth-mfa/SignInCompletionFailure';
import { TotpCodeForm } from '../components/auth-mfa/TotpCodeForm';
import { TotpSetupForm } from '../components/auth-mfa/TotpSetupForm';
import { useWebAuth } from '../contexts/WebAuthContext';
import { useSignInFlow } from '../hooks/use-sign-in-flow';
import { sanitizeInternalRedirect } from '../services/auth-redirect';

/**
 * Webログインページ
 *
 * ダウンロードページなどの保護されたページにアクセスするための
 * Webブラウザ用ログインページ
 */
export function WebLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, checkAuth } = useWebAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  // 完了したら認証状態を更新する。認証済みになると下の useEffect がリダイレクト先へ遷移する。
  // 認証状態を確認できなければ失敗として扱い、もう一度試せるようにする
  const flow = useSignInFlow({
    onSignedIn: async () => {
      if (!(await checkAuth())) {
        throw new Error('サインイン後の認証状態を確認できませんでした');
      }
    },
  });

  // リダイレクト先URL（内部パスのみ許可 — オープンリダイレクト対策）
  const redirectUrl = sanitizeInternalRedirect(searchParams.get('redirect'));

  // 既に認証済みの場合はリダイレクト
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(redirectUrl, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, redirectUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await flow.submitCredentials(email, password);
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    await flow.submitNewPassword(newPassword);
  };

  // 初期ローディング中
  if (isLoading) {
    return (
      <AuthLayout title="ログイン" subtitle="認証を確認中...">
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-muted border-l-primary-500 rounded-full animate-spin" />
        </div>
      </AuthLayout>
    );
  }

  if (flow.step.kind === 'done' && flow.error) {
    return (
      <AuthLayout title="ログイン">
        <SignInCompletionFailure
          error={flow.error}
          submitting={flow.submitting}
          onRetry={flow.retryCompletion}
        />
      </AuthLayout>
    );
  }

  if (flow.step.kind === 'totpSetup') {
    return (
      <AuthLayout
        title="認証アプリの登録"
        subtitle="ログインには、パスワードに加えて認証アプリのコードが必要です"
      >
        <TotpSetupForm
          setupUri={flow.step.setupUri}
          sharedSecret={flow.step.sharedSecret}
          submitting={flow.submitting}
          error={flow.error}
          onSubmit={flow.submitTotpSetupCode}
        />
      </AuthLayout>
    );
  }

  if (flow.step.kind === 'totpCode') {
    return (
      <AuthLayout title="確認コードの入力" subtitle="認証アプリのコードを入力してください">
        <TotpCodeForm
          submitting={flow.submitting}
          error={flow.error}
          onSubmit={flow.submitTotpCode}
          onRestart={flow.restart}
        />
      </AuthLayout>
    );
  }

  if (flow.step.kind === 'setupComplete') {
    return (
      <AuthLayout title="登録完了">
        <MfaSetupCompleteNotice onContinue={flow.acknowledgeSetupComplete} />
      </AuthLayout>
    );
  }

  if (flow.step.kind === 'newPassword') {
    return (
      <AuthLayout
        title="新しいパスワードを設定"
        subtitle="初回ログインのため、新しいパスワードを設定してください"
      >
        <form onSubmit={handleNewPassword} className="space-y-6">
          <AuthErrorMessage error={flow.error} />
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-foreground mb-2">
              新しいパスワード
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="新しいパスワードを入力"
            />
          </div>
          <button
            type="submit"
            disabled={flow.submitting}
            className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {flow.submitting ? '設定中...' : 'パスワードを設定'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="ログイン" subtitle="ダウンロードページにアクセスするにはログインが必要です">
      <form onSubmit={handleSubmit} className="space-y-6">
        <AuthErrorMessage
          error={flow.error}
          signUpHref={`/signup?redirect=${encodeURIComponent(redirectUrl)}`}
        />

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            メールアドレス
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="example@email.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
            パスワード
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="パスワードを入力"
          />
        </div>

        <button
          type="submit"
          disabled={flow.submitting}
          className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {flow.submitting || flow.step.kind === 'done' ? 'ログイン中...' : 'ログイン'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-muted-foreground">
          アカウントをお持ちでない方は
          <a
            href={`/signup?redirect=${encodeURIComponent(redirectUrl)}`}
            className="text-primary-400 hover:text-primary-300 transition-colors ml-1"
          >
            アカウント作成
          </a>
        </p>
      </div>
    </AuthLayout>
  );
}
